/**
 * A preprocessor plugin that detects and anonymizes Finnish social security numbers in messages.
 * Uses the finnish-ssn package for validation of SSN format and checksums.
 * Preserves the separator character (-, + or A) for better readability.
 *
 * Validation rules:
 * - DDMMYY: Valid date (including leap years)
 * - Separator: - (1900s), + (1800s), or A (2000s)
 * - Individual number: 002-899
 * - Control character: 0-9 or A-Y
 *
 * @class FinnishSSNAnonymizer
 * @implements {PreprocessorPlugin}
 *
 * @example
 * ```typescript
 * const anonymizer = new FinnishSSNAnonymizer();
 * chatClient.addPreprocessorPlugin(anonymizer);
 * ```
 */
export class FinnishSSNAnonymizer {
    constructor() {
        /**
         * Unique identifier for the plugin
         */
        this.name = 'finnish-ssn-anonymizer';
    }
    /**
     * Validates if a date string (DDMMYY) represents a valid date
     * @param dateStr - Date string in DDMMYY format
     * @param separator - The century separator (-, +, or A)
     * @returns boolean indicating if the date is valid
     */
    isValidDate(dateStr, separator) {
        const day = parseInt(dateStr.substring(0, 2));
        const month = parseInt(dateStr.substring(2, 4));
        const year = parseInt(dateStr.substring(4, 6));
        // Early validation of basic ranges
        if (month < 1 || month > 12)
            return false;
        if (day < 1)
            return false;
        // Determine century based on separator
        let fullYear;
        switch (separator) {
            case '+': // 1800s
                fullYear = year + 1800;
                break;
            case '-': // 1900s
                fullYear = year + 1900;
                break;
            case 'A': // 2000s
                fullYear = year + 2000;
                break;
            default:
                return false;
        }
        // Check days in month (including leap year February)
        const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
        // Adjust February for leap years
        if (fullYear % 4 === 0 && (fullYear % 100 !== 0 || fullYear % 400 === 0)) {
            daysInMonth[1] = 29; // Leap year
        }
        // Validate the day is within the valid range for the month
        // month - 1 is safe here because we already validated month range above
        return day <= daysInMonth[month - 1];
    }
    /**
     * Validates if the individual number is in valid range (002-899)
     * @param individualNumber - Three digit individual number
     * @returns boolean indicating if the number is valid
     */
    isValidIndividualNumber(individualNumber) {
        // Ensure the number is exactly 3 digits
        if (individualNumber.length !== 3)
            return false;
        const num = parseInt(individualNumber);
        return num >= 2 && num <= 899;
    }
    /**
     * Process a message to anonymize Finnish SSNs.
     * Only processes user messages, leaving system and assistant messages unchanged.
     * Replaces valid SSNs with X characters but preserves the separator character.
     *
     * @param {string} content - The message to process
     * @returns {Promise<string>} The message with anonymized SSNs
     *
     * @example
     * Input: "My SSN is 230590-323B and email: test(230503A323B)@example.com"
     * Output: "My SSN is XXXXXX-XXXX and email: test(XXXXXXAXXXX)@example.com"
     */
    async process(content) {
        let processedContent = content;
        // Matches Finnish SSN patterns:
        // \d{6} - Six digits for date of birth (DDMMYY)
        // [-+A] - Followed by a separator character (-, + or A)
        // \d{3} - Followed by three digits (individual number)
        // [\dA-Y] - Followed by a control character (digit or 0-9 or A-Y)
        const potentialSSNRegex = /\d{6}[-+A]\d{3}[\dA-Y]/g;
        const matches = processedContent.match(potentialSSNRegex) || [];
        for (const match of matches) {
            try {
                // Extract parts for validation
                const datePart = match.substring(0, 6);
                const separator = match.charAt(6);
                const individualPart = match.substring(7, 10);
                // Control character is part of regex but not explicitly validated here
                // Validate date and individual number
                if (this.isValidDate(datePart, separator) &&
                    this.isValidIndividualNumber(individualPart)) {
                    // If all validations pass, anonymize the SSN
                    const anonymized = 'X'.repeat(6) + separator + 'X'.repeat(4);
                    // Use replaceAll to handle multiple occurrences correctly within the loop
                    // Avoids issues with split/join if the SSN appears multiple times consecutively
                    processedContent = processedContent.replaceAll(match, anonymized);
                }
                // If validation fails, do nothing, leave the original match in the string
            }
            catch (e) {
                // Log potential errors during processing? For now, just continue.
                console.error(`Error processing potential SSN match "${match}":`, e);
                continue;
            }
        }
        return processedContent;
    }
}
//# sourceMappingURL=FinnishSSNAnonymizer.js.map